import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './richTextEditor.css';
import './CtaBlockBlot';
import {
  Box,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { QUILL_TOOLBAR } from '../../utils/const/quillModules';
import UploadRequest from '../../requests/api/upload';
import {
  BLOG_CONTENT,
  SUCCESS_STORY_CONTENT,
} from '../../utils/const/uploadFolders';
import CtaBlockModal from './CtaBlockModal';

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/gif';
// Mismo tope que aplica multer en el servidor (server/utils/uploadImage.js)
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_SIZE_LABEL = '5MB';

function RichTextEditor({ value, onChange, folder }) {
  const { enqueueSnackbar } = useSnackbar();
  const quillRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [ctaModalOpen, setCtaModalOpen] = useState(false);
  const [ctaEditValues, setCtaEditValues] = useState(null);
  const ctaInsertIndexRef = useRef(0);

  // El handler de Quill se registra una sola vez, así que lee folder y
  // enqueueSnackbar desde refs para no quedarse con valores viejos.
  const folderRef = useRef(folder);
  const enqueueSnackbarRef = useRef(enqueueSnackbar);

  useEffect(() => { folderRef.current = folder; }, [folder]);
  useEffect(() => { enqueueSnackbarRef.current = enqueueSnackbar; }, [enqueueSnackbar]);

  const handleImageUpload = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // La posición del cursor se guarda antes de abrir el explorador de
    // archivos, porque al volver el editor ya perdió el foco.
    const selection = editor.getSelection();
    const insertIndex = selection ? selection.index : editor.getLength();

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', ACCEPTED_IMAGE_TYPES);

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        enqueueSnackbarRef.current(`La imagen supera los ${MAX_IMAGE_SIZE_LABEL}`, {
          variant: 'warning',
        });
        return;
      }

      setUploading(true);
      const response = await UploadRequest.postUploadImage({
        file,
        folder: folderRef.current,
      });
      setUploading(false);

      const url = response.data?.data?.url;
      if (response.success && url) {
        editor.insertEmbed(insertIndex, 'image', url, 'user');
        editor.setSelection(insertIndex + 1, 0, 'silent');
      } else {
        enqueueSnackbarRef.current(
          response.data?.errorMessage || 'No se pudo subir la imagen',
          { variant: 'warning' },
        );
      }
    };

    input.click();
  }, []);

  // El bloque CTA es no-editable in-place (contenteditable=false en el blot):
  // guarda la posición del cursor antes de abrir el modal y sólo ahí se inserta.
  const handleOpenCtaModal = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const selection = editor.getSelection();
    ctaInsertIndexRef.current = selection ? selection.index : editor.getLength();
    setCtaEditValues(null);
    setCtaModalOpen(true);
  }, []);

  const handleEditorClick = useCallback((event) => {
    const ctaNode = event.target.closest?.('.ql-cta-block');
    if (!ctaNode) return;

    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const blot = ReactQuill.Quill.find(ctaNode);
    if (!blot) return;

    ctaInsertIndexRef.current = editor.getIndex(blot);
    setCtaEditValues({
      title: ctaNode.dataset.ctaTitle || '',
      subtitle: ctaNode.dataset.ctaSubtitle || '',
      buttonLabel: ctaNode.dataset.ctaLabel || '',
      href: ctaNode.dataset.ctaHref || '',
    });
    setCtaModalOpen(true);
  }, []);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    const root = editor?.root;
    if (!root) return undefined;

    root.addEventListener('click', handleEditorClick);
    return () => root.removeEventListener('click', handleEditorClick);
  }, [handleEditorClick]);

  const handleInsertCta = useCallback((values) => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const index = ctaInsertIndexRef.current;

    if (ctaEditValues) {
      editor.deleteText(index, 1, 'user');
      editor.insertEmbed(index, 'ctaBlock', values, 'user');
      editor.setSelection(index + 1, 0, 'silent');
    } else {
      editor.insertEmbed(index, 'ctaBlock', values, 'user');
      editor.insertText(index + 1, '\n', 'user');
      editor.setSelection(index + 2, 0, 'silent');
    }
    setCtaModalOpen(false);
  }, [ctaEditValues]);

  // ReactQuill no soporta que modules cambie de identidad entre renders.
  const modules = useMemo(() => ({
    toolbar: {
      container: QUILL_TOOLBAR,
      handlers: { image: handleImageUpload, ctaBlock: handleOpenCtaModal },
    },
  }), [handleImageUpload, handleOpenCtaModal]);

  return (
    <Box>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        modules={modules}
        value={value}
        onChange={onChange}
      />
      {uploading ? (
        <Box display="flex" alignItems="center" mt={1} style={{ gap: 8 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="textSecondary">
            Subiendo imagen...
          </Typography>
        </Box>
      ) : null}
      <CtaBlockModal
        open={ctaModalOpen}
        onClose={() => setCtaModalOpen(false)}
        onSubmit={handleInsertCta}
        initialValues={ctaEditValues}
      />
    </Box>
  );
}

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  folder: PropTypes.oneOf([BLOG_CONTENT, SUCCESS_STORY_CONTENT]).isRequired,
};

RichTextEditor.defaultProps = {
  value: '',
};

export default RichTextEditor;
